import React, { FunctionComponent } from 'react';
import { useDispatch } from 'react-redux';
import { useVisibilityObserver } from '@noths/polaris-client-react-hooks';
import { Breakpoint, grid, maxWidthContainer } from '@noths/polaris-client-ribbons-base';
import { Media } from '@noths/polaris-client-ribbons-design-system';
import { atLeastOneValueTruthy } from '@noths/polaris-client-utils';

import { FileLink } from 'src/components/atoms/FileLink';
import {
  ThumbnailOrientation,
  ThumbnailsContainer,
} from 'src/components/organisms/ProductImagesCarousel';
import { sectionVisible } from 'src/redux/visibilityTracking';
import { Product } from 'src/types';
import { removeHtmlElementsFromString } from 'src/utils';
import * as copy from '../constants/copy';
import { PRODUCT_DESCRIPTION_VISIBILITY_NAME } from '../tracking/events';
import { ExpandableToggleContainer } from './ExpandableToggleContainer/ExpandableToggleContainer';
import { ProductOverview } from './ProductOverview';
import * as styles from '../styles/productDescription.styles';

export interface ProductDescriptionProps {
  product: Product;
  handleThumbnailClick: (imageIndex: number) => void;
  onExpandProductDescriptionToggle: (isOpen: boolean) => void;
}

export const ProductDescription: FunctionComponent<ProductDescriptionProps> = ({
  product: {
    code,
    description,
    otherInformation,
    variationsAvailable,
    specialFeatures,
    allowsGiftWrap,
    freeGiftWrap,
    dimensions,
    materials,
    ingredients,
  },
  handleThumbnailClick,
  onExpandProductDescriptionToggle,
}: ProductDescriptionProps) => {
  const overviewProps = { allowsGiftWrap, freeGiftWrap, ...specialFeatures };
  const dispatch = useDispatch();
  const { observedElementRef: productDescriptionRef } = useVisibilityObserver(() =>
    dispatch(sectionVisible({ sectionName: PRODUCT_DESCRIPTION_VISIBILITY_NAME })),
  );

  return (
    <>
      <Media at={Breakpoint.S} css={styles.thumbnails}>
        <ThumbnailsContainer
          orientation={ThumbnailOrientation.HORIZONTAL}
          onThumbnailClick={handleThumbnailClick}
          location="Carousel"
        />
      </Media>
      <ExpandableToggleContainer
        toggleContainerId="product-description"
        onToggle={onExpandProductDescriptionToggle}
      >
        <div ref={productDescriptionRef} css={[grid, maxWidthContainer, styles.descriptionGrid]}>
          <div css={styles.description}>
            <h2 css={styles.heading}>{copy.PRODUCT_DESCRIPTION_HEADING}</h2>
            <p
              dangerouslySetInnerHTML={{
                __html: removeHtmlElementsFromString(description, ['p', 'b', 'strong']),
              }}
            />
            <div
              dangerouslySetInnerHTML={{
                __html: removeHtmlElementsFromString(otherInformation, ['b', 'strong']),
              }}
            />
            {variationsAvailable && (
              <>
                <h2 css={styles.heading}>{copy.PRODUCT_VARIATIONS_HEADING}</h2>
                <div
                  dangerouslySetInnerHTML={{
                    __html: removeHtmlElementsFromString(variationsAvailable, ['b', 'strong']),
                  }}
                />
              </>
            )}
          </div>
          {atLeastOneValueTruthy(overviewProps) && (
            <div css={[styles.sideSection, styles.overview]}>
              <h2 css={styles.heading}>{copy.PRODUCT_OVERVIEW_HEADING}</h2>
              <ProductOverview {...overviewProps} />
            </div>
          )}
          <div css={[styles.sideSection, styles.extraDetails]}>
            <h2 css={styles.heading}>{copy.PRODUCT_DIMENTIONS_HEADING}</h2>
            <div
              dangerouslySetInnerHTML={{
                __html: removeHtmlElementsFromString(dimensions, ['b', 'strong']),
              }}
            />
            <h2 css={styles.heading}>{copy.MADE_FROM_HEADING}</h2>
            <div
              dangerouslySetInnerHTML={{
                __html: removeHtmlElementsFromString(materials, ['b', 'strong']),
              }}
            />
            {ingredients && (
              <div css={styles.ingredientsInfo}>
                <FileLink {...ingredients} title={copy.INGREDIENTS_TITLE} />
              </div>
            )}
            <h2 css={styles.heading}>{copy.PRODUCT_CODE_HEADING}</h2>
            <p>{code}</p>
          </div>
        </div>
      </ExpandableToggleContainer>
    </>
  );
};
